import React, { useState } from "react";
import { InfiniteScroll } from "@/components/InfiniteScroll";
import { useNavigate } from "react-router-dom";
import { useLoading } from "@/lib/LoadingHook";
import { AdminPageLayout } from "../../../layouts/AdminPageLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormError } from "@/components/FormError";
import {
  getModules,
  createModule,
  deleteModule,
} from "@/services/admin/modules.service";
import {
  Module,
  CreateModuleInputs,
  ModuleStatus,
} from "@/services/admin/modules.interfaces";
import { useAppToast } from "@/hooks/useAppToast";
import { useRefresher } from "@/components/grids/DataGridComponent";
import { toKebabCase, generateInternalName } from "@/utils/stringNormalization";

// The local module schema should match the CreateModuleInputs interface from the service
const moduleSchema = z.object({
  name: z
    .string({ required_error: "Nome do módulo é obrigatório" })
    .min(3, { message: "Nome deve ter pelo menos 3 caracteres" }),
  internalName: z
    .string({ required_error: "Nome interno é obrigatório" })
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
      message: "Nome interno deve estar em formato kebab-case (ex: modulo-basico-1)",
    }),
  description: z
    .string({ required_error: "Descrição é obrigatória" })
    .min(10, { message: "Descrição deve ter pelo menos 10 caracteres" }),
  summarizedDescription: z.string().optional(),
  teaser: z.string().optional(),
  title: z.string().optional(),
  status: z.enum(["Draft", "Published", "Archived"]).optional(),
});

interface ToolbarProps {
  onCreateClick: () => void;
}

interface CreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateModuleInputs) => void;
  isLoading?: boolean;
}

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const Toolbar: React.FC<ToolbarProps> = ({ onCreateClick }) => (
  <section id="tools" className="mb-6">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex flex-1 items-center space-x-4">
        <div className="relative flex-1 max-w-xl">
          <Input
            type="search"
            placeholder="Buscar módulos..."
            className="pl-10"
          />
          <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"></i>
        </div>
        <button className="flex items-center space-x-2 rounded-lg border border-neutral-300 px-4 py-2">
          <i className="fa-solid fa-filter text-neutral-600"></i>
          <span>Filtros</span>
        </button>
      </div>
      <Button
        onClick={onCreateClick}
        className="flex items-center space-x-2 rounded-lg bg-neutral-900 px-4 py-2 text-white"
      >
        <i className="fa-solid fa-plus"></i>
        <span>Criar Módulo</span>
      </Button>
    </div>
  </section>
);

interface ModuleCardProps {
  module: Module;
  onDeleteClick: () => void;
}

const ModuleCard: React.FC<ModuleCardProps> = ({ module, onDeleteClick }) => {
  const navigate = useNavigate();

  return (
    <div className="rounded-lg border bg-white shadow-sm">
      <div className="relative h-48 mb-4 rounded-t-lg overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1546410531-bb4caa6b424d?w=800&auto=format&fit=crop"
          alt="Capa do módulo"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black opacity-50"></div>
        <div className="absolute bottom-4 left-4 right-4">
          <span className={`text-white text-sm px-2 py-1 rounded ${
            module.status === "Published" ? "bg-green-500" : "bg-red-500"
          }`}>
            {module.status}
          </span>
        </div>
      </div>
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{module.name}</h3>
          <div className="flex space-x-2">
            <button
              onClick={() => navigate(`/admin/modules/${module.id}`)}
              className="rounded-full p-2 hover:bg-neutral-100"
            >
              <i className="fa-solid fa-pen text-neutral-600"></i>
            </button>
            <button
              onClick={onDeleteClick}
              className="rounded-full p-2 hover:bg-neutral-100"
            >
              <i className="fa-solid fa-trash text-neutral-600"></i>
            </button>
          </div>
        </div>
        <p className="text-neutral-600 mb-4">{module.description}</p>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <i className="fa-solid fa-list-check text-neutral-500"></i>
            <span className="text-neutral-600">{module.exercises?.length || 0} Exercícios</span>
          </div>
          <div className="flex items-center space-x-2">
            <i className="fa-solid fa-sort-numeric-up text-neutral-500"></i>
            <span className="text-neutral-600">Ordem: {module.order}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateModal: React.FC<CreateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateModuleInputs>({
    resolver: zodResolver(moduleSchema),
    defaultValues: {
      status: "Draft" as ModuleStatus,
    }
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 ${
        isOpen ? "" : "hidden"
      }`}
    >
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-2xl rounded-lg bg-white p-6">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-xl font-semibold">Criar Novo Módulo</h2>
            <button
              onClick={handleClose}
              className="rounded-full p-2 hover:bg-neutral-100"
            >
              <i className="fa-solid fa-times text-neutral-600"></i>
            </button>
          </div>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-2">
              <Label htmlFor="name">Nome do Módulo</Label>
              <Input 
                id="name" 
                {...register("name")}
                onBlur={(e) => {
                  // Auto-preenchimento do internalName apenas uma vez
                  const internalNameField = document.getElementById('internalName') as HTMLInputElement;
                  if (internalNameField && !internalNameField.value) {
                    internalNameField.value = toKebabCase(e.target.value);
                  }
                }}
              />
              <FormError message={errors.name?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="internalName">Nome Interno</Label>
              <Input 
                id="internalName" 
                {...register("internalName")}
                placeholder="ex: modulo-basico-1"
                onBlur={(e) => {
                  const normalized = toKebabCase(e.target.value);
                  e.target.value = normalized;
                }}
              />
              <FormError message={errors.internalName?.message} />
              <p className="text-sm text-muted-foreground">
                Nome interno para URLs e identificação. Será normalizado automaticamente para kebab-case.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                rows={3}
                {...register("description")}
              />
              <FormError message={errors.description?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="summarizedDescription">Descrição Resumida</Label>
              <Textarea
                id="summarizedDescription"
                rows={2}
                {...register("summarizedDescription")}
              />
              <FormError message={errors.summarizedDescription?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="teaser">Teaser</Label>
              <Textarea 
                id="teaser" 
                rows={2}
                {...register("teaser")} 
              />
              <FormError message={errors.teaser?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Título</Label>
              <Input id="title" {...register("title")} />
              <FormError message={errors.title?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                className="w-full rounded-md border border-input bg-background px-3 py-2"
                {...register("status")}
              >
                <option value="Draft">Rascunho</option>
                <option value="Published">Publicado</option>
                <option value="Archived">Arquivado</option>
              </select>
              <FormError message={errors.status?.message} />
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" type="button" onClick={handleClose}>
                Cancelar
              </Button>
              <Button type="submit" isLoading={isLoading}>
                Salvar Módulo
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const DeleteModal: React.FC<DeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => (
  <div
    className={`fixed inset-0 bg-black bg-opacity-50 ${isOpen ? "" : "hidden"}`}
  >
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6">
        <div className="mb-6 text-center">
          <i className="fa-solid fa-exclamation-triangle text-4xl text-neutral-600 mb-4"></i>
          <h2 className="text-xl font-semibold">Excluir Módulo?</h2>
          <p className="mt-2 text-neutral-600">
            Tem certeza que deseja excluir este módulo? Esta ação não pode ser desfeita.
          </p>
        </div>
        <div className="flex justify-center space-x-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-neutral-300 px-4 py-2"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="rounded-lg bg-neutral-900 px-4 py-2 text-white"
          >
            Excluir
          </button>
        </div>
      </div>
    </div>
  </div>
);

export const AdminModulesPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const { isLoading, whenLoading } = useLoading();
  const toast = useAppToast();

  const refreshHandle = useRefresher();

  const fetchModules = async () => {
    try {
      // No packageId needed anymore
      return await getModules();
    } catch (error) {
      toast.error("Falha ao carregar módulos");
      console.error("Error loading modules:", error);
      return [];
    }
  };

  const renderItem = (module: Module, key: React.Key) => (
    <ModuleCard
      key={key}
      module={module}
      onDeleteClick={() => {
        setSelectedModuleId(module.id);
        setIsDeleteModalOpen(true);
      }}
    />
  );

  const itemKey = (module: Module) => module.id;

  const handleCreateModule = async (data: CreateModuleInputs) => {
    const success = await whenLoading(async () => {
      try {
        // No packageId needed anymore
        await createModule(data);
        toast.success("Módulo criado com sucesso");
        refreshHandle.refresh();
        return true;
      } catch (error) {
        toast.error("Falha ao criar módulo");
        console.error("Error creating module:", error);
        return false;
      }
    });

    if (success) {
      setIsCreateModalOpen(false);
    }
  };

  const handleDeleteModule = async () => {
    if (!selectedModuleId) return;

    const success = await whenLoading(async () => {
      try {
        // No packageId needed anymore
        await deleteModule(selectedModuleId);
        toast.success("Módulo excluído com sucesso");
        refreshHandle.refresh();
        return true;
      } catch (error) {
        toast.error("Falha ao excluir módulo");
        console.error("Error deleting module:", error);
        return false;
      }
    });

    if (success) {
      setIsDeleteModalOpen(false);
      setSelectedModuleId(null);
    }
  };

  return (
    <AdminPageLayout title="Módulos">
      <main id="main" className="py-6">
        <Toolbar onCreateClick={() => setIsCreateModalOpen(true)} />
        <section id="modules-grid">
          <InfiniteScroll<Module>
            refreshHandle={refreshHandle}
            fetchItems={fetchModules}
            renderItem={renderItem}
            itemKey={itemKey}
            layout="grid"
            gridCols={{ default: 2, lg: 3 }}
          />
        </section>
      </main>
      <CreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateModule}
        isLoading={isLoading}
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteModule}
      />
    </AdminPageLayout>
  );
}; 